'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { receptionQueries } from '../services/reception.queries';
import type { ReceptionSession } from '../types';
import {
  ReceptionWizardHeader,
  THREE_STEP_WIZARD
} from './reception-wizard-header';
import { StepQrScan } from './step-qr-scan';
import { StepBoxCount } from './step-box-count';
import { StepDataVerification } from './step-data-verification';

const STEP_SUBTITLE_KEYS = [
  'wizardSubtitleQrScan',
  'wizardSubtitleBoxCount',
  'wizardSubtitleDataVerification'
] as const;

export function ReceptionInitializeWizard() {
  const t = useTranslations('reception');
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [localSession, setLocalSession] = useState<ReceptionSession | null>(
    null
  );
  const [shipmentId, setShipmentId] = useState<number | null>(null);

  // Once we discover the shipment via QR, load its detail
  const { data: shipmentResponse } = useQuery({
    ...receptionQueries.detail(shipmentId ?? 0),
    enabled: !!shipmentId
  });

  const shipment = shipmentResponse?.data ?? null;
  const session = localSession;

  const markComplete = (step: number) => {
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
    setCurrentStep(step + 1);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setCompletedSteps([]);
    setLocalSession(null);
    setShipmentId(null);
  };

  return (
    <div className='flex flex-1 flex-col items-center space-y-6'>
      {/* Top bar */}
      <div className='flex w-full items-start justify-between'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>
            {t('pageTitle')}
          </h1>
          <p className='text-muted-foreground mt-1 text-sm'>
            {t('wizardStepLabel', { step: currentStep + 1 })}
            {' · '}
            {t(STEP_SUBTITLE_KEYS[currentStep])}
          </p>
        </div>
        <div className='flex items-center gap-3'>
          {shipmentId && (
            <Badge
              variant='outline'
              className='border-primary/30 bg-primary/5 text-primary gap-1.5 px-3 py-1.5'
            >
              <span className='bg-primary h-2 w-2 rounded-full' />
              {t('sessionActive')}
            </Badge>
          )}
          <Button variant='outline' size='sm' onClick={handleReset}>
            <RotateCcw className='me-2 h-4 w-4' />
            {t('reset')}
          </Button>
        </div>
      </div>

      {/* Stepper */}
      <ReceptionWizardHeader
        currentStep={currentStep}
        completedSteps={completedSteps}
        steps={THREE_STEP_WIZARD}
      />

      {/* Step 0: QR scan in discovery mode (no shipment) */}
      {currentStep === 0 && (
        <StepQrScan
          session={session}
          onVerified={(newSession: ReceptionSession) => {
            setLocalSession(newSession);
            setShipmentId(newSession.shipment_id);
          }}
          onComplete={() => {
            markComplete(0);
          }}
        />
      )}

      {currentStep === 1 && session && shipment && (
        <StepBoxCount
          session={session}
          expectedBoxCount={shipment.boxes_count ?? shipment.box_count}
          shipmentNumber={shipment.shipment_number}
          onComplete={() => markComplete(1)}
          onBack={() => setCurrentStep(0)}
          onSessionUpdate={setLocalSession}
        />
      )}

      {currentStep === 2 && session && (
        <StepDataVerification
          session={session}
          onBack={() => setCurrentStep(1)}
          onSessionUpdate={setLocalSession}
        />
      )}
    </div>
  );
}
